// packages/ui/src/tools/toolsStore.ts
import { create } from 'zustand';
import { ToolRegistry } from './ToolRegistry';

export interface ToolInstance {
  id: string;
  toolId: string;
  /** The displayed tab title. Either the auto title or a user-pinned name. */
  title: string;
  /**
   * Latest tool-provided title (e.g. a terminal's xterm OSC title, which shells
   * set to the running command / cwd). Tracked even while a manual name is
   * pinned, so clearing the pin can revert to it.
   */
  autoTitle?: string;
  /**
   * True when the user explicitly renamed this tab. While set, auto title
   * updates are recorded into `autoTitle` but do NOT change the displayed
   * `title`. Clearing it (rename to empty) reverts the title to `autoTitle`.
   */
  titleManual?: boolean;
  busy?: boolean;
  /** Tool-specific initial state, set at open-time. */
  initialState?: unknown;
}

export interface ToolsState {
  panel: { visible: boolean; height: number };
  instances: ToolInstance[];
  activeInstanceId: string | null;

  openTool(toolId: string, initialState?: unknown): string;
  closeInstance(instanceId: string): void;
  setActiveInstance(instanceId: string): void;
  togglePanel(): void;
  showPanel(): void;
  hidePanel(): void;
  setPanelHeight(px: number): void;
  /** Auto channel: the tool's own title (e.g. xterm OSC). Honors a manual pin. */
  setInstanceTitle(instanceId: string, title: string): void;
  /** Manual channel: a user rename. Non-empty pins the tab; empty reverts to auto. */
  renameInstance(instanceId: string, rawTitle: string): void;
  setInstanceBusy(instanceId: string, busy: boolean): void;
  /**
   * Replace an instance's persisted open-state. Used to strip one-shot fields
   * (e.g. a terminal's `initialCommand`) after they've been consumed, so the
   * tools snapshot can never carry them back into a restored session and
   * re-run them.
   */
  setInstanceInitialState(instanceId: string, initialState: unknown): void;
  closeAll(): void;
}

const DEFAULT_HEIGHT = 260;
const MIN_HEIGHT = 120;
function maxHeight(): number {
  if (typeof window === 'undefined') return 800;
  return Math.floor(window.innerHeight * 0.7);
}
function clampHeight(px: number): number {
  return Math.max(MIN_HEIGHT, Math.min(maxHeight(), px));
}

let nextInstanceCounter = 0;
function nextInstanceId(toolId: string): string {
  nextInstanceCounter += 1;
  return `${toolId}-${nextInstanceCounter}-${Date.now().toString(36)}`;
}

function nextTitle(toolId: string, existing: ToolInstance[]): string {
  const def = ToolRegistry.getById(toolId);
  const base = def?.title ?? toolId;
  const count = existing.filter((i) => i.toolId === toolId).length + 1;
  return `${base} ${count}`;
}

export const useToolsStore = create<ToolsState>((set, get) => ({
  panel: { visible: false, height: DEFAULT_HEIGHT },
  instances: [],
  activeInstanceId: null,

  openTool(toolId, initialState) {
    const def = ToolRegistry.getById(toolId);
    if (!def) throw new Error(`openTool: unknown tool "${toolId}"`);
    const { instances } = get();

    if (def.instances === 'single') {
      const existing = instances.find((i) => i.toolId === toolId);
      if (existing) {
        set({ activeInstanceId: existing.id, panel: { ...get().panel, visible: true } });
        return existing.id;
      }
    }

    const id = nextInstanceId(toolId);
    const instance: ToolInstance = {
      id,
      toolId,
      title: nextTitle(toolId, instances),
      initialState,
    };
    set({
      instances: [...instances, instance],
      activeInstanceId: id,
      panel: { ...get().panel, visible: true },
    });
    return id;
  },

  closeInstance(instanceId) {
    const { instances, activeInstanceId } = get();
    const idx = instances.findIndex((i) => i.id === instanceId);
    if (idx < 0) return;
    const next = instances.filter((i) => i.id !== instanceId);
    let nextActive = activeInstanceId;
    if (activeInstanceId === instanceId) {
      nextActive = next[Math.max(0, idx - 1)]?.id ?? next[0]?.id ?? null;
    }
    set({ instances: next, activeInstanceId: nextActive });
  },

  setActiveInstance(instanceId) {
    if (get().instances.some((i) => i.id === instanceId)) {
      set({ activeInstanceId: instanceId });
    }
  },

  togglePanel() {
    set({ panel: { ...get().panel, visible: !get().panel.visible } });
  },
  showPanel() {
    set({ panel: { ...get().panel, visible: true } });
  },
  hidePanel() {
    set({ panel: { ...get().panel, visible: false } });
  },
  setPanelHeight(px) {
    set({ panel: { ...get().panel, height: clampHeight(px) } });
  },
  setInstanceTitle(instanceId, title) {
    // Auto channel (the tool's own setTitle + xterm OSC title events). Always
    // records the latest auto title, but only updates the DISPLAYED title when
    // the user hasn't pinned a custom name. No-ops when nothing changes so the
    // frequent OSC title stream doesn't churn the store / re-render the strip.
    set({
      instances: get().instances.map((i) => {
        if (i.id !== instanceId) return i;
        const display = i.titleManual ? i.title : title;
        if (i.autoTitle === title && i.title === display) return i;
        return { ...i, autoTitle: title, title: display };
      }),
    });
  },
  renameInstance(instanceId, rawTitle) {
    // Manual channel (double-click / context-menu "Rename"). A non-empty name
    // pins the tab so auto title updates stop changing it; an empty name clears
    // the pin and reverts to the latest auto title, so the tab follows the
    // running command again.
    const trimmed = rawTitle.trim();
    set({
      instances: get().instances.map((i) => {
        if (i.id !== instanceId) return i;
        if (trimmed.length === 0) {
          return { ...i, titleManual: false, title: i.autoTitle ?? i.title };
        }
        return { ...i, titleManual: true, title: trimmed };
      }),
    });
  },
  setInstanceBusy(instanceId, busy) {
    set({
      instances: get().instances.map((i) => (i.id === instanceId ? { ...i, busy } : i)),
    });
  },
  setInstanceInitialState(instanceId, initialState) {
    set({
      instances: get().instances.map((i) =>
        i.id === instanceId ? { ...i, initialState } : i,
      ),
    });
  },
  closeAll() {
    set({ instances: [], activeInstanceId: null });
  },
}));

// ───────────────── Session persistence ─────────────────

/**
 * Serializable snapshot of the tool panel: which instances are open (id +
 * toolId + title + tool-specific initial state), the active instance, and the
 * panel geometry. Only plain JSON-safe data — live PTY sessions / xterm state
 * are NOT captured here; a restored terminal re-spawns from its `state` (cwd).
 */
export interface ToolsSnapshot {
  panel: { visible: boolean; height: number };
  instances: Array<{
    id: string;
    toolId: string;
    title: string;
    /** Whether the title was a user rename (so restore won't auto-clobber it). */
    titleManual?: boolean;
    state: unknown;
  }>;
  activeInstanceId: string | null;
}

/** Capture the current tools state as a serializable snapshot. */
export function toolsToSnapshot(): ToolsSnapshot {
  const s = useToolsStore.getState();
  return {
    panel: s.panel,
    instances: s.instances.map((i) => ({
      id: i.id,
      toolId: i.toolId,
      title: i.title,
      titleManual: i.titleManual,
      state: i.initialState ?? null,
    })),
    activeInstanceId: s.activeInstanceId,
  };
}

/**
 * Rehydrate the tools state from a snapshot. A missing snapshot (older session)
 * is a no-op so restore is backward-compatible. Each restored instance carries
 * its `state` back as `initialState`, so the tool's `deserialize`/`createState`
 * re-creates the live view (e.g. a terminal re-spawns in its saved cwd).
 */
export function toolsFromSnapshot(snap: ToolsSnapshot | undefined): void {
  if (!snap) return;
  useToolsStore.setState({
    panel: snap.panel,
    instances: snap.instances.map((i) => ({
      id: i.id,
      toolId: i.toolId,
      title: i.title,
      // A restored user-pinned title stays pinned, so the terminal's initial
      // auto title on remount doesn't overwrite it.
      titleManual: i.titleManual,
      initialState: i.state ?? undefined,
    })),
    activeInstanceId: snap.activeInstanceId,
  });
}
